import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Select,
  Textarea,
  NumberInput,
  NumberInputField,
  useToast,
  FormHelperText,
  InputGroup,
  InputRightAddon,
  useColorModeValue,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ProductCategory } from '../../types/product';
import { useProducts } from '../../contexts/ProductContext';
import { useAuth } from '../../contexts/AuthContext';

const categories: { label: string; value: ProductCategory; group: string }[] = [
  { label: 'Seeds & Seedlings', value: 'seeds', group: 'Agro Inputs & Equipment' },
  { label: 'Fertilizers & Soil Care', value: 'fertilizers', group: 'Agro Inputs & Equipment' },
  { label: 'Pesticides & Chemicals', value: 'pesticides', group: 'Agro Inputs & Equipment' },
  { label: 'Generators & Power', value: 'generators', group: 'Agro Inputs & Equipment' },
  { label: 'Water Pumps & Irrigation', value: 'pumps', group: 'Agro Inputs & Equipment' },
  { label: 'Irrigation Systems', value: 'irrigation', group: 'Agro Inputs & Equipment' },
  { label: 'Heavy Machinery & Tractors', value: 'machinery', group: 'Agro Inputs & Equipment' },
  { label: 'Farm Tools & Implements', value: 'farm_tools', group: 'Agro Inputs & Equipment' },
  { label: 'Animal Feeds & Supplements', value: 'animal_feeds', group: 'Agro Inputs & Equipment' },
  { label: 'Veterinary Supplies', value: 'veterinary', group: 'Agro Inputs & Equipment' },
  { label: 'Grains & Cereals', value: 'grains', group: 'Farm Produce' },
  { label: 'Vegetables', value: 'vegetables', group: 'Farm Produce' },
  { label: 'Fruits', value: 'fruits', group: 'Farm Produce' },
  { label: 'Livestock & Animals', value: 'livestock', group: 'Farm Produce' },
  { label: 'Dairy & Poultry', value: 'dairy', group: 'Farm Produce' },
  { label: 'Other', value: 'other', group: 'Other' },
];

const equipmentCategories = [
  'generators',
  'pumps',
  'irrigation',
  'machinery',
  'farm_tools',
];

const AddProduct: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { addProduct } = useProducts();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory | ''>('');

  const isEquipment = equipmentCategories.includes(selectedCategory);
  const isLivestock = selectedCategory === 'livestock';
  const isFarmProduce = ['grains', 'vegetables', 'fruits', 'dairy'].includes(selectedCategory);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'You must be logged in to add products.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      navigate('/login');
      return;
    }

    // Create form data
    const formData = new FormData(e.target as HTMLFormElement);
    const images = formData.getAll('images').filter((value) => value instanceof File && value.size > 0);
    if (images.length > 5) {
      toast({ title: 'Choose up to 5 images', status: 'error', duration: 5000 });
      return;
    }
    setIsSubmitting(true);

    // Category specific field cleanups
    if (isLivestock) {
      formData.set('age', String(Number(formData.get('age'))));
      formData.set('breed', formData.get('breed') as string);
      formData.delete('harvestDate');
      formData.delete('condition');
      formData.delete('brand');
    } else if (isEquipment) {
      formData.delete('harvestDate');
      formData.delete('age');
      formData.delete('breed');
    } else if (isFarmProduce) {
      const rawHarvestDate = formData.get('harvestDate') as string;
      if (rawHarvestDate) {
        formData.set('harvestDate', new Date(rawHarvestDate).toISOString());
      }
      formData.delete('age');
      formData.delete('breed');
      formData.delete('condition');
    } else {
      formData.delete('harvestDate');
      formData.delete('age');
      formData.delete('breed');
    }
    
    // Convert numeric values
    formData.set('quantity', String(Number(formData.get('quantity'))));
    formData.set('price', String(Number(formData.get('price'))));

    try {
      await addProduct(formData);

      toast({
        title: 'Product Added Successfully',
        description: 'Your product has been listed on the marketplace.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Redirect based on user role
      if (user.role === 'agro_dealer') {
        navigate('/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add product. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box py={8}>
      <Container maxW="container.md">
        <VStack spacing={8}>
          <Heading>
            {user?.role === 'agro_dealer' ? 'List Supply / Equipment' : 'Add New Product'}
          </Heading>

          <Box
            as="form"
            onSubmit={handleSubmit}
            w="full"
            bg={useColorModeValue('white', 'gray.700')}
            p={8}
            borderRadius="lg"
            boxShadow="base"
          >
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Product / Item Name</FormLabel>
                <Input name="name" placeholder="e.g., 5.5HP Water Pump, Hybrid Seed Maize, Honda Generator 3KVA" />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select 
                  name="category" 
                  placeholder="Select category"
                  onChange={(e) => setSelectedCategory(e.target.value as ProductCategory)}
                >
                  <optgroup label="Agro Inputs & Equipment">
                    {categories.filter(c => c.group === 'Agro Inputs & Equipment').map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Farm Produce">
                    {categories.filter(c => c.group === 'Farm Produce').map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Other">
                    {categories.filter(c => c.group === 'Other').map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </optgroup>
                </Select>
              </FormControl>

              {/* Brand and Condition for Equipment */}
              {isEquipment && (
                <>
                  <FormControl>
                    <FormLabel>Brand / Manufacturer</FormLabel>
                    <Input name="brand" placeholder="e.g., Honda, STIHL, Grundfos, John Deere" />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Item Condition</FormLabel>
                    <Select name="condition" defaultValue="new">
                      <option value="new">Brand New</option>
                      <option value="used">Used / Pre-owned</option>
                      <option value="refurbished">Refurbished</option>
                    </Select>
                  </FormControl>
                </>
              )}

              <FormControl isRequired>
                <FormLabel>Quantity Available</FormLabel>
                <NumberInput min={0}>
                  <NumberInputField name="quantity" placeholder="Enter available quantity" />
                </NumberInput>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Unit</FormLabel>
                <Select name="unit" placeholder="Select unit">
                  <option value="piece">Piece / Item</option>
                  <option value="unit">Unit</option>
                  <option value="set">Set / Kit</option>
                  <option value="pack">Pack / Packet</option>
                  <option value="bag">Bag / Sack</option>
                  <option value="box">Box / Carton</option>
                  <option value="litre">Litre (L)</option>
                  <option value="kg">Kilogram (kg)</option>
                  <option value="ton">Tonne (ton)</option>
                  <option value="crate">Crate</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Price per Unit (USD)</FormLabel>
                <InputGroup>
                  <NumberInput min={0} w="full">
                    <NumberInputField name="price" placeholder="Enter price per unit" />
                  </NumberInput>
                  <InputRightAddon>USD</InputRightAddon>
                </InputGroup>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Location</FormLabel>
                <Input name="location" placeholder="Enter your location" />
              </FormControl>

              {/* Show different fields based on category */}
              {selectedCategory === 'livestock' ? (
                <>
                  <FormControl isRequired>
                    <FormLabel>Animal Age</FormLabel>
                    <InputGroup>
                      <NumberInput min={0} w="full">
                        <NumberInputField name="age" placeholder="Enter age" />
                      </NumberInput>
                      <InputRightAddon>months</InputRightAddon>
                    </InputGroup>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Breed</FormLabel>
                    <Input name="breed" placeholder="Enter breed/type" />
                  </FormControl>
                </>
              ) : (
                <FormControl isRequired>
                  <FormLabel>Harvest Date</FormLabel>
                  <Input name="harvestDate" type="date" />
                </FormControl>
              )}

              <FormControl isRequired>
                <FormLabel>Description</FormLabel>
                <Textarea
                  name="description"
                  placeholder="Describe your product (quality, specifications, minimum order, etc.)"
                  rows={4}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Product Images (Max 5)</FormLabel>
                <Input
                  name="images"
                  type="file"
                  accept="image/*"
                  multiple
                />
                <FormHelperText>Upload up to 5 clear images of your product</FormHelperText>
              </FormControl>

              <Button
                type="submit"
                colorScheme="brand"
                size="lg"
                w="full"
                mt={6}
                isLoading={isSubmitting}
                loadingText="Adding Product..."
              >
                Add Product
              </Button>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default AddProduct;
